
export enum UserRole {
  SAAS_ADMIN = 'SAAS_ADMIN', // Dono da plataforma (Você)
  OWNER = 'OWNER',           // Dono do restaurante
  CASHIER = 'CASHIER',       // Caixa (PDV)
  WAITER = 'WAITER'          // Garçom (Comanda)
}

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  CANCELED = 'CANCELED',
  TRIAL = 'TRIAL'
}

export type POSMode = 'STANDARD' | 'DISTRIBUTOR' | 'MARKET';
export type StaffAccessProfile = 'EMPLOYEE' | 'MANAGER' | 'COUNTER';
export type StaffPermission = 'POS' | 'PRODUCTS' | 'STOCK' | 'REPORTS' | 'SETTINGS' | 'KITCHEN' | 'DELIVERY';

export interface DeliveryZone {
  name: string;
  fee: number;
}

export interface DeliverySettings {
  isOpen: boolean;
  allowPickup: boolean;
  openingTime?: string;
  closingTime?: string;
  minimumOrder: number;
  estimatedMinutes: number;
  defaultDeliveryFee: number;
  zones: DeliveryZone[];
}

// Representa um Cliente (Bar/Restaurante) na plataforma
export interface Company {
  id: string;
  name: string;
  document: string; // CNPJ
  email: string;    // Email do dono
  billingEmail?: string | null;
  subscriptionStatus: SubscriptionStatus;
  plan: 'BASIC' | 'PRO' | 'ENTERPRISE';
  monthlyPriceOverride?: number | null;
  nextBillingDate: string;
  createdAt: string;
  tableCount: number;
  referredById?: string;
  referredByName?: string;
  contactPhone?: string;
  ownerAdminId?: string;
  ownerAdminName?: string;
  billingProvider?: string;
  mercadoPagoPreapprovalId?: string;
  mercadoPagoStatus?: string;
  lastPaymentAt?: string;
  useShiftManagement?: boolean;
  shiftStartHour?: number;
  shiftStartMinute?: number;
  shiftEndHour?: number;
  shiftEndMinute?: number;
  useStockManagement?: boolean;
  posMode?: POSMode;
  useCounterMode?: boolean;
  useWaiterApp?: boolean;
  useKitchenDisplay?: boolean;
  useSplitBill?: boolean;
  useServiceFee?: boolean;
  useScale?: boolean;
  useDelivery?: boolean;
  deliverySettings?: DeliverySettings;
  useWhatsappAutomation?: boolean;
  useInvoicePhotoOcr?: boolean;
  invoicePhotoOcrMonthlyLimit?: number | null;
  useSubLogin?: boolean;
  waiterAccessCode?: string;
}

export type BillingPlanKey = Company['plan'];
export type SubscriptionNoticeRecurrence = 'ONCE_PER_CYCLE' | 'MILESTONES' | 'WEEKLY_AFTER_EXPIRY' | 'PROGRESSIVE_AFTER_EXPIRY' | 'DISABLED';

export interface BillingPlanConfig {
  plan: BillingPlanKey;
  name: string;
  price: number;
  features: Record<string, boolean>;
}

export interface BillingConfig {
  provider: 'MERCADOPAGO';
  publicKeyConfigured: boolean;
  checkoutConfigured: boolean;
  featureCatalog: Record<string, string>;
  plans: BillingPlanConfig[];
  subscriptionNoticeRecurrence: SubscriptionNoticeRecurrence;
  textract?: {
    configured: boolean;
    globalMonthlyLimit: number;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  password?: string; // Em produção, isso seria hash
  role: UserRole;
  companyId?: string; // Link para a empresa (undefined se for SAAS_ADMIN)
  adminLevel?: 0 | 1 | 2; // 0=Super, 1=Gerente, 2=Aprovador (Only for SAAS_ADMIN)
  canSendEmails?: boolean;
  canLeadTeam?: boolean;
  phone?: string;
  position?: string;
  accessProfile?: StaffAccessProfile;
  accessPermissions?: StaffPermission[];
  supervisorId?: string;
  supervisorName?: string;
}

export interface AdminTeamMessage {
  id: string;
  authorId: string;
  authorName: string;
  targetAdminId?: string;
  targetCompanyId?: string;
  message: string;
  priority: 'NORMAL' | 'IMPORTANT' | 'URGENT';
  status: 'OPEN' | 'DONE';
  createdAt: string;
}

export interface Manager {
  id: string;
  companyId: string;
  name: string;
  password?: string;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  description: string;
  image: string;
  barcode?: string;
  sku?: string;
  brand?: string;
  ncm?: string;
  cfop?: string;
  location?: string;
  purchasePrice?: number;
  costPrice?: number;
  profitMargin?: number;
  wholesaleQuantity?: number;
  wholesalePrice?: number;
  grossWeight?: number;
  netWeight?: number;
  expirationDate?: string;
  active?: boolean;
  allowSale?: boolean;
  composition?: boolean;
  manufactured?: boolean;
  fractioned?: boolean;
  weighable?: boolean;
  trackStock?: boolean;
  stockQuantity?: number;
  lowStockThreshold?: number;
  stockProductId?: string;
  stockDeductQuantity?: number;
  stockIngredients?: StockIngredient[];
  recipeYield?: number;
  preparationMethod?: string;
  stockPackageProductId?: string;
  stockPackageSize?: number;
  stockUnit?: 'UN' | 'G' | 'KG' | 'ML' | 'L';
  internalUseOnly?: boolean;
  saleUnit?: 'UNIT' | 'KG';
  productionArea?: 'AUTO' | 'BAR' | 'KITCHEN' | 'BOTH';
  availableForDelivery?: boolean;
  modifierGroups?: ProductModifierGroup[];
}

export interface ProductModifierOption {
  id: string;
  name: string;
  price: number;
}

export interface ProductModifierGroup {
  id: string;
  name: string;
  minSelections: number;
  maxSelections: number;
  options: ProductModifierOption[];
}

export interface ProductModifierSelection {
  groupId: string;
  optionIds: string[];
}

export interface StockIngredient {
  productId: string;
  quantity: number;
  unit?: 'UN' | 'G' | 'KG' | 'ML' | 'L';
}

export interface InvoiceProductMapping {
  id: string;
  companyId: string;
  supplierProductCode: string;
  supplierProductName?: string;
  supplierBarcode?: string;
  supplierNcm?: string;
  supplierCfop?: string;
  supplierUnit?: string;
  lastUnitPrice?: number;
  lastTotal?: number;
  productId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvoiceSupplier {
  name: string;
  document?: string;
  stateRegistration?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface Supplier {
  id: string;
  companyId: string;
  name: string;
  document?: string;
  stateRegistration?: string;
  email?: string;
  phone?: string;
  address?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SupplierProductPrice {
  id: string;
  companyId: string;
  supplierId: string;
  productId: string;
  supplierProductName?: string;
  supplierSku?: string;
  unitPrice: number;
  packageQuantity?: number;
  minimumQuantity?: number;
  lastQuotedAt?: string;
  notes?: string;
  supplier?: Pick<Supplier, 'id' | 'companyId' | 'name' | 'phone' | 'email'>;
  product?: Pick<Product, 'id' | 'name' | 'category' | 'image'>;
  createdAt?: string;
  updatedAt?: string;
}

export type ProductionSector = 'KITCHEN' | 'BAR';

export interface ProductionPrintMark {
  printedAt: Date;
  reprintCount: number;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  discount?: number;
  notes?: string;
  waiterName?: string;
  sentAt?: Date;
  productionArea?: 'AUTO' | 'BAR' | 'KITCHEN' | 'BOTH';
  saleUnit?: 'UNIT' | 'KG';
  stockProductId?: string;
  stockDeductQuantity?: number;
  stockIngredients?: StockIngredient[];
  stockRecipeYield?: number;
  stockPackageProductId?: string;
  stockPackageSize?: number;
  productionPrints?: Partial<Record<ProductionSector, ProductionPrintMark>>;
  status: 'PENDING' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CANCELED';
}

export interface Payment {
  id: string;
  method: 'Dinheiro' | 'Cartão Crédito' | 'Cartão Débito' | 'Pix';
  amount: number;
  tenderedAmount?: number;
  timestamp: Date;
}

export interface Table {
  revision?: number;
  epoch?: string;
  id: number;
  label: string;
  name?: string;
  status: 'FREE' | 'OCCUPIED' | 'PAYING';
  total: number;
  discount?: number;
  serviceFee: boolean; // Controls the 10%
  serviceFeeChosen?: boolean;
  items: OrderItem[];
  payments: Payment[];
  openedAt?: Date;
}

export interface Transaction {
  id: string;
  companyId: string; // Importante para separar dados
  tableLabel: string;
  items: OrderItem[];
  payments: Payment[];
  total: number;
  discount?: number;
  closedAt: Date;
  stockAlreadyReserved?: boolean;
}

export interface TableCloseResult {
  order: Transaction;
  table: Table;
  change: number;
}

export interface FinancialEntry {
  id: string;
  companyId: string;
  type: 'EXPENSE';
  category: string;
  description: string;
  amount: number;
  occurredAt: Date;
  notes?: string;
  createdAt?: Date;
}

export interface AuditLogEntry {
  id: string;
  companyId: string;
  action: string;
  managerName: string;
  timestamp: Date;
}

export interface InviteCode {
  id: string;
  code: string;
  expires_at: string;
  used: boolean;
  generatedById?: string;
  generatedByName?: string;
}

export interface CashMovement {
  id: string;
  type: 'DEPOSIT' | 'WITHDRAWAL';
  amount: number;
  reason: string;
  timestamp: Date;
  managerName: string;
}

export interface CashierShift {
  id: string;
  companyId: string;
  status: 'OPEN' | 'CLOSED';
  openedAt: string;
  closedAt?: string;
  openingFloat: number;
  openingDetails?: any;
  closingBalance?: number;
  movements: CashMovement[];
  finalReport?: any;
}

export type DeliveryOrderStatus = 'RECEIVED' | 'ACCEPTED' | 'PREPARING' | 'READY' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'CANCELED';

export type DeliveryPaymentMethod = 'Dinheiro' | 'Cartão Crédito' | 'Cartão Débito' | 'Pix';
export type DeliveryPaymentStatus = 'PENDING' | 'PAID';

export type DeliveryFulfillmentType = 'DELIVERY' | 'PICKUP';

export interface DeliveryCustomer {
  name: string;
  phone: string;
  address: string;
  number: string;
  neighborhood: string;
  complement?: string;
  reference?: string;
  wantsWhatsappUpdates?: boolean;
}

export interface DeliveryOrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  notes?: string;
  modifierSelections?: ProductModifierSelection[];
}

export interface DeliveryOrder {
  id: string;
  publicToken?: string;
  companyId: string;
  companyName: string;
  customer: DeliveryCustomer;
  items: DeliveryOrderItem[];
  fulfillmentType: DeliveryFulfillmentType;
  paymentMethod: DeliveryPaymentMethod;
  changeFor?: number;
  paymentStatus?: DeliveryPaymentStatus;
  financialTransactionId?: string;
  paidAt?: string;
  deliveryFee: number;
  subtotal: number;
  total: number;
  notes?: string;
  status: DeliveryOrderStatus;
  createdAt: string;
  updatedAt: string;
}
